import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Sun, Moon, ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";
import { useTheme } from "@/components/ThemeProvider";

const NAV = [
    {
        label: "Protocol",
        to: "/network",
        dropdown: [
            { label: "Protocol Overview", to: "/network" },
            { label: "Hybrid Orchestration", to: "/network#orchestration" },
            { label: "Architecture", to: "/network#architecture" },
            { label: "Whitepaper", to: "/whitepaper" },
            { label: "Patent Filing", to: "/whitepaper#patent" },
        ],
    },
    { label: "Compute", to: "/network#compute" },
    { label: "Storage", to: "/storage" },
    { label: "AI Cloud", to: "/ai-compute" },
    { label: "Layer-1", to: "/layer-1" },
    { label: "Nodes", to: "/nodes" },
    {
        label: "Developers",
        to: "/developers",
        dropdown: [
            { label: "Docs", to: "https://decloudx.gitbook.io/decloudx", external: true },
            { label: "SDKs", to: "/developers#sdks" },
            { label: "CLI", to: "/developers#cli" },
            { label: "APIs", to: "/developers#apis" },
            { label: "Smart Contracts", to: "/layer-1#contracts" },
            { label: "Explorer", to: "/layer-1#explorer" },
            { label: "GitHub", to: "https://x.com/DeCloudX_com", external: true },
        ],
    },
    { label: "$DCX", to: "/token" },
];

const JOIN_DROPDOWN = [
    { label: "Become a Node Operator", to: "/contact?role=node_operator" },
    { label: "Join as Developer", to: "/contact?role=developer" },
    { label: "Partner With Us", to: "/contact?role=partner" },
    { label: "Investor Interest", to: "/contact?role=investor" },
];

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [joinOpen, setJoinOpen] = useState(false);
    const { theme, setTheme } = useTheme();
    const dropdownTimer = useRef();
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); setActiveDropdown(null); setJoinOpen(false); }, [location.pathname]);

    const showDropdown = (label) => {
        clearTimeout(dropdownTimer.current);
        setActiveDropdown(label);
    };
    const hideDropdown = () => {
        dropdownTimer.current = setTimeout(() => setActiveDropdown(null), 120);
    };

    const isActive = (to) => location.pathname === to;

    return (
        <header
            data-testid="site-nav"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled ? "glass-strong border-b border-line" : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between gap-4">
                <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="nav-logo">
                    <Logo variant={theme === "dark" ? "white" : "gradient"} className="h-9 md:h-10" />
                </Link>

                <nav className="hidden lg:flex flex-1 items-center justify-center gap-0.5" data-testid="primary-nav">
                    {NAV.map((item) => (
                        <div
                            key={item.label}
                            className="relative"
                            onMouseEnter={() => item.dropdown && showDropdown(item.label)}
                            onMouseLeave={() => item.dropdown && hideDropdown()}
                        >
                            <Link
                                to={item.to}
                                data-testid={`nav-link-${item.label.toLowerCase().replace(/[^a-z0-9]/g, "")}`}
                                className={`inline-flex items-center gap-1 px-3 py-2 text-sm transition-colors font-medium whitespace-nowrap ${
                                    isActive(item.to) ? "text-ink" : "text-muted-ink hover:text-ink"
                                }`}
                            >
                                {item.label}
                                {item.dropdown && <ChevronDown size={12} strokeWidth={2} className={`transition-transform ${activeDropdown === item.label ? "rotate-180" : ""}`} />}
                            </Link>
                            {item.dropdown && activeDropdown === item.label && (
                                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 min-w-[220px] glass-strong rounded-xl p-2 border border-line shadow-lg" data-testid={`dropdown-${item.label.toLowerCase()}`}>
                                    {item.dropdown.map((d, i) => (
                                        d.external ? (
                                            <a
                                                key={i}
                                                href={d.to}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="block px-3 py-2 text-sm text-muted-ink hover:text-ink hover:bg-card-muted rounded-md transition-colors"
                                            >
                                                {d.label}
                                            </a>
                                        ) : (
                                            <Link
                                                key={i}
                                                to={d.to}
                                                className="block px-3 py-2 text-sm text-muted-ink hover:text-ink hover:bg-card-muted rounded-md transition-colors"
                                            >
                                                {d.label}
                                            </Link>
                                        )
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </nav>

                <div className="flex items-center gap-2 shrink-0">
                    <Link to="/whitepaper" className="hidden xl:inline-flex text-xs text-muted-ink hover:text-ink transition-colors px-3 py-2 font-mono uppercase tracking-[0.15em]" data-testid="nav-whitepaper">Whitepaper</Link>
                    <a href="https://decloudx.gitbook.io/decloudx" target="_blank" rel="noreferrer" className="hidden xl:inline-flex text-xs text-muted-ink hover:text-ink transition-colors px-3 py-2 font-mono uppercase tracking-[0.15em]" data-testid="nav-docs">Docs</a>
                    <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")} className="w-9 h-9 rounded-full bg-card border border-line hover:border-line-strong shadow-sm flex items-center justify-center text-muted-ink hover:text-ink transition-all" aria-label="Toggle theme" data-testid="theme-toggle">
                        {theme === "dark" ? <Sun size={15} strokeWidth={1.5} /> : <Moon size={15} strokeWidth={1.5} />}
                    </button>
                    <div className="relative hidden md:block" onMouseEnter={() => setJoinOpen(true)} onMouseLeave={() => setJoinOpen(false)}>
                        <button className="btn-primary" data-testid="nav-cta-join">Join Network <ChevronDown size={12} strokeWidth={2} /></button>
                        {joinOpen && (
                            <div className="absolute top-full right-0 mt-1 min-w-[240px] glass-strong rounded-xl p-2 border border-line shadow-lg" data-testid="dropdown-join">
                                {JOIN_DROPDOWN.map((d, i) => (
                                    <Link key={i} to={d.to} className="block px-3 py-2 text-sm text-muted-ink hover:text-ink hover:bg-card-muted rounded-md transition-colors">{d.label}</Link>
                                ))}
                            </div>
                        )}
                    </div>
                    <button onClick={() => setOpen(!open)} className="lg:hidden text-ink p-2" data-testid="nav-mobile-toggle" aria-label="Toggle menu">
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>
            {open && (
                <div className="lg:hidden glass-strong border-t border-line px-5 sm:px-6 py-5 space-y-0.5 max-h-[calc(100vh-4rem)] overflow-y-auto">
                    {NAV.map((item) => (
                        <MobileNavItem key={item.label} item={item} />
                    ))}
                    <Link to="/blog" className="block py-3 text-muted-ink text-sm border-b border-line">Blog</Link>
                    <Link to="/whitepaper" className="block py-3 text-muted-ink text-sm border-b border-line">Whitepaper</Link>
                    <Link to="/about" className="block py-3 text-muted-ink text-sm border-b border-line">About</Link>
                    <Link to="/contact" className="block w-full text-center btn-primary mt-5 justify-center" data-testid="mobile-nav-cta">Join Network</Link>
                </div>
            )}
        </header>
    );
}

function MobileNavItem({ item }) {
    const [expanded, setExpanded] = useState(false);
    const slug = item.label.toLowerCase().replace(/[^a-z0-9]/g, "");
    if (!item.dropdown) {
        return (
            <Link to={item.to} className="flex items-center justify-between py-3 text-ink-2 hover:text-ink border-b border-line text-sm" data-testid={`mobile-nav-${slug}`}>
                {item.label}
            </Link>
        );
    }
    return (
        <div className="border-b border-line">
            <div className="flex items-stretch">
                <Link to={item.to} className="flex-1 py-3 text-ink-2 hover:text-ink text-sm" data-testid={`mobile-nav-${slug}`}>
                    {item.label}
                </Link>
                <button
                    onClick={() => setExpanded((v) => !v)}
                    className="px-3 text-muted-ink hover:text-ink"
                    aria-label={`Toggle ${item.label} submenu`}
                    data-testid={`mobile-nav-${slug}-toggle`}
                >
                    <ChevronDown size={14} strokeWidth={2} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
                </button>
            </div>
            {expanded && (
                <div className="pb-2 pl-3 space-y-0.5">
                    {item.dropdown.map((d, i) => (
                        d.external ? (
                            <a key={i} href={d.to} target="_blank" rel="noreferrer" className="block py-2 text-[13px] text-muted-ink hover:text-ink">
                                {d.label}
                            </a>
                        ) : (
                            <Link key={i} to={d.to} className="block py-2 text-[13px] text-muted-ink hover:text-ink">
                                {d.label}
                            </Link>
                        )
                    ))}
                </div>
            )}
        </div>
    );
}
